import { useFlags } from '@unleash/nextjs';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import {
  DashboardSidebar,
  getFilteredMenu,
  getHrefPaths,
  getMenuMatchesOrPaths,
  isMenuActive,
  MenuCollapse,
  MenuLink,
  OnlyWhenUnleashFlagValue,
  pathnameOnly,
  SidebarRoutesContextProps,
  useDashboardSidebarCtx,
  useSidebarRoutesCtx,
} from '@/common/components/Sidebar';
import { MENU_LIST, MOBILE_VIEW_WIDTH } from '@/common/constants';
import { useSidebar } from '@/common/contexts/useSidebar';
import useWindowSize from '@/common/hooks/useWindowSize';
import HomeIcon from '@/icons/menu/HomeIcon';
import NotificationIcon from '@/icons/menu/NotificationIcon';
import useMyUserCompanyQuery from '@/modules/company/hooks/queries/useMyUserCompanyQuery';
import useNotificationStatusQuery from '@/modules/notifications/hooks/queries/useNotificationStatusQuery';
import useGeneralPermissionModal from '@/modules/permissions/hooks/useGeneralPermissionModal';

const DashboardSidebarRoutesListItem = ({
  rootLink,
  onClickLink = () => {},
}: {
  rootLink: MenuLink | MenuCollapse;
  onClickLink?: (e: React.MouseEvent<HTMLAnchorElement>, path: string) => void;
}) => {
  const { onlyWhen: onlyWhenConditions, currentPathname: pathname } =
    useSidebarRoutesCtx();
  const availableMatchesAndPaths = getMenuMatchesOrPaths(
    rootLink,
    onlyWhenConditions,
    {
      menuHierarchyLevel: 'root',
    },
  );

  const isRootMenuActive = isMenuActive(availableMatchesAndPaths, pathname, {
    exactMatch: rootLink.exactMatch,
  });
  const isCollapsible = rootLink.type === 'collapse';
  const isLinkActive = isRootMenuActive && !isCollapsible;

  return (
    <DashboardSidebar.RoutesListItem isCollapsible={isCollapsible}>
      <DashboardSidebar.RoutesListItemTooltip label={rootLink.label}>
        <DashboardSidebar.RoutesListItemLink asChild>
          <Link
            href={getHrefPaths(rootLink, onlyWhenConditions)}
            target={rootLink.target}
            onClick={(e) =>
              onClickLink &&
              onClickLink(e, getHrefPaths(rootLink, onlyWhenConditions))
            }
          >
            {!!rootLink.icon && (
              <DashboardSidebar.RoutesIconRoot>
                <rootLink.icon
                  isOpen={isRootMenuActive}
                  isActive={isRootMenuActive}
                />
              </DashboardSidebar.RoutesIconRoot>
            )}
            <DashboardSidebar.RoutesListItemText isActive={isLinkActive}>
              {rootLink.label}
            </DashboardSidebar.RoutesListItemText>
          </Link>
        </DashboardSidebar.RoutesListItemLink>
      </DashboardSidebar.RoutesListItemTooltip>

      {rootLink.type === 'collapse' && (
        <DashboardSidebar.RoutesCollapsibleRoot open={isRootMenuActive}>
          {rootLink.children.map((link) => {
            const availableMatchesAndPaths = getMenuMatchesOrPaths(
              link,
              onlyWhenConditions,
              {
                menuHierarchyLevel: 'child',
              },
            );
            const isActive = isMenuActive(availableMatchesAndPaths, pathname, {
              exactMatch: rootLink.exactMatch,
            });
            return (
              <DashboardSidebar.RoutesListItem
                key={link.id}
                isCollapsibleChildItem
              >
                <DashboardSidebar.RoutesListItemLink asChild>
                  <Link
                    href={getHrefPaths(link, onlyWhenConditions)}
                    target={link.target}
                    onClick={(e) =>
                      onClickLink &&
                      onClickLink(e, getHrefPaths(link, onlyWhenConditions))
                    }
                  >
                    <DashboardSidebar.RoutesListItemText isActive={isActive}>
                      {link.label}
                    </DashboardSidebar.RoutesListItemText>
                  </Link>
                </DashboardSidebar.RoutesListItemLink>
              </DashboardSidebar.RoutesListItem>
            );
          })}
        </DashboardSidebar.RoutesCollapsibleRoot>
      )}
    </DashboardSidebar.RoutesListItem>
  );
};

const SidebarContent = () => {
  const router = useRouter();

  const { menus, currentPathname } = useSidebarRoutesCtx();
  const { sidebarExpanded } = useDashboardSidebarCtx();

  const isHomeActive =
    currentPathname === '/' || currentPathname === '/notifications';

  const { setMobileCollapsed } = useSidebar();
  const [width] = useWindowSize();

  const { data: notificationStatus } = useNotificationStatusQuery();

  const {
    canAccessTalentDB,
    canAccessTalentSearch,
    canAccessAnalytics,
    setRequestPermissionModalVisible,
    generatePermissionPayload,
    generatePermissionResource,
    setPermissionPayload,
    setPermissionResource,
  } = useGeneralPermissionModal();

  const handleClickLink = (e, path) => {
    e.preventDefault();

    if (width <= MOBILE_VIEW_WIDTH) {
      setMobileCollapsed(true);
    }

    const talentSearchPage = path.includes('/explore-talents');
    const talentDBPage = path.includes('/talent-database');
    const analyticsPage = path.includes('/analytics');

    const shouldOpenGeneralPermissionModal =
      (talentDBPage && !canAccessTalentDB) ||
      (talentSearchPage && !canAccessTalentSearch) ||
      (analyticsPage && !canAccessAnalytics);

    if (shouldOpenGeneralPermissionModal) {
      const payload = generatePermissionPayload(path);
      setPermissionPayload(payload);
      const resource = generatePermissionResource(path);
      setPermissionResource(resource);
      setRequestPermissionModalVisible(true);
      return;
    }

    router.push(path);
  };

  return (
    <>
      <DashboardSidebar.RoutesListItem>
        <DashboardSidebar.RoutesListItemTooltip
          label={'Home'}
          className="w-full flex justify-between"
        >
          <DashboardSidebar.RoutesListItemLink
            asChild
            className="justify-between"
          >
            <>
              <Link href={'/'} className="flex items-center gap-2">
                <DashboardSidebar.RoutesIconRoot>
                  {isHomeActive ? (
                    <HomeIcon.Primary className="shrink-0 text-xs text-violet-500 !size-3" />
                  ) : (
                    <HomeIcon className="shrink-0 text-xs text-neutral-500 !size-3" />
                  )}
                </DashboardSidebar.RoutesIconRoot>
                <DashboardSidebar.RoutesListItemText isActive={isHomeActive}>
                  Home
                </DashboardSidebar.RoutesListItemText>
              </Link>

              {sidebarExpanded && (
                <Link href="/notifications">
                  <NotificationIcon
                    notificationIsActive={Boolean(
                      notificationStatus?.hasNotification,
                    )}
                  />
                </Link>
              )}
            </>
          </DashboardSidebar.RoutesListItemLink>
        </DashboardSidebar.RoutesListItemTooltip>
      </DashboardSidebar.RoutesListItem>

      {menus.map((rootMenu, idx) => {
        return (
          <DashboardSidebar.RoutesSectionContainer key={rootMenu.id}>
            {idx !== 0 && <DashboardSidebar.RoutesSectionDivider />}

            <DashboardSidebar.RoutesSectionTitle>
              {rootMenu.label}
            </DashboardSidebar.RoutesSectionTitle>

            <DashboardSidebar.RoutesList>
              {!!rootMenu.children &&
                rootMenu.children.map((rootLinks) => {
                  return (
                    <DashboardSidebarRoutesListItem
                      key={rootLinks.id}
                      rootLink={rootLinks}
                      onClickLink={handleClickLink}
                    />
                  );
                })}
            </DashboardSidebar.RoutesList>
          </DashboardSidebar.RoutesSectionContainer>
        );
      })}
    </>
  );
};

const SidebarRoutes = () => {
  const rawUnleashFlag = useFlags();

  if (!rawUnleashFlag) return null;

  const router = useRouter();
  const currentPathname = useMemo(
    () => pathnameOnly(router.asPath),
    [router.asPath],
  );

  const onlyWhenUnleashFlagValue = Object.fromEntries(
    rawUnleashFlag.map((flag) => [
      flag.name as OnlyWhenUnleashFlagValue,
      flag.enabled,
    ]),
  ) as Record<OnlyWhenUnleashFlagValue, boolean>;

  const { data: userCompanyData } = useMyUserCompanyQuery();

  const onlyWhen: SidebarRoutesContextProps['onlyWhen'] = {
    unleashFlag: onlyWhenUnleashFlagValue,
    userCompanyData: userCompanyData?.data?.result,
  };

  const filteredMenu = getFilteredMenu(MENU_LIST, onlyWhen);

  return (
    <DashboardSidebar.RoutesRoot
      value={{
        onlyWhen,
        currentPathname,
        menus: filteredMenu,
      }}
    >
      <SidebarContent />
    </DashboardSidebar.RoutesRoot>
  );
};

export default SidebarRoutes;
